import React, { useState } from "react";
import { Upload, File, Download, Trash2, X, Check, AlertCircle, Loader2 } from "lucide-react";
import { apiClient } from "../../../config/api";
import API_CONFIG from "../../../config/api";

// Helper to get Clerk token for authenticated requests
const getAuthToken = async () => {
  try {
    const clerk = window.Clerk;
    if (clerk?.session) {
      return await clerk.session.getToken();
    }
  } catch (error) {
    console.warn("Failed to get Clerk token:", error);
  }
  return null;
};

export default function Attachments({ incident }) {
  const incidentName =
    incident?.incidentName ||
    incident?._raw?.incidentName ||
    incident?.id ||
    incident?._raw?.id ||
    null;

  const initialAttachments = Array.isArray(incident?.attachments)
    ? incident.attachments
    : Array.isArray(incident?._raw?.attachments)
    ? incident._raw.attachments
    : [];

  const resolveAttachmentUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    const base = API_CONFIG.BASE_URL?.replace(/\/+$/, "") || "";
    const path = url.startsWith("/") ? url : `/${url}`;
    return `${base}${path}`;
  };

  const [attachments, setAttachments] = useState(
    initialAttachments.map((att, idx) => ({
      id: att.id || att.attachmentId || idx,
      attachmentId: att.attachmentId || att.id || null, // Store the actual attachmentId for API calls
      name: att.name || att.fileName || "Attachment",
      size: att.size || att.fileSize || att.fileSizeBytes || att.length || "N/A",
      type: att.type || att.mimeType || "application/octet-stream",
      uploadedBy: att.uploadedBy || att.owner || "Unknown",
      uploadedAt: att.uploadedAt || att.uploadedOn || att.createdAt || att.createdOn || att.timestamp || "-",
      url: resolveAttachmentUrl(att.url || att.downloadUrl || att.path || att.href),
    }))
  );

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
    setShowUploadModal(true);
    setUploadError(null);
    setUploadSuccess(false);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    // Check if incident has an identifier
    if (!incidentName) {
      setUploadError("Incident identifier (incidentName) is required to upload files");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      // Upload files one by one
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        // Create FormData
        const formData = new FormData();
        formData.append('file', file);

        // Update progress for current file
        const baseProgress = (i / selectedFiles.length) * 100;
        setUploadProgress(Math.round(baseProgress));

        console.log(`Uploading file ${i + 1}/${selectedFiles.length}:`, file.name);
        console.log("Incident Name:", incidentName);

        // Make API call
        const uploadUrl = API_CONFIG.ENDPOINTS.INCIDENTS_UPLOAD(incidentName);
        console.log("Upload URL:", uploadUrl);

        const response = await apiClient.post(uploadUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const fileProgress = (progressEvent.loaded / progressEvent.total) * 100;
            const totalProgress = baseProgress + (fileProgress / selectedFiles.length);
            setUploadProgress(Math.round(totalProgress));
          }
        });

        console.log("Upload response:", response.data);

        // Add uploaded file to attachments list
        const newAttachment = {
          id: response.data?.attachmentId || response.data?.id || attachments.length + i + 1,
          attachmentId: response.data?.attachmentId || response.data?.id || null,
          name: file.name,
          size: formatFileSize(file.size),
          type: file.type || 'application/octet-stream',
          uploadedBy: 'Current User',
          uploadedAt: new Date().toISOString().replace('T', ' ').slice(0, 19),
          url: resolveAttachmentUrl(
            response.data?.url ||
            response.data?.downloadUrl ||
            response.data?.path ||
            response.data?.href
          ) || "#"
        };

        setAttachments((prev) => [...prev, newAttachment]);
      }

      // Success
      setUploadProgress(100);
      setUploadSuccess(true);

      // Close modal after a short delay
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        setSelectedFiles([]);
        setShowUploadModal(false);
        setUploadSuccess(false);
      }, 1500);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.response?.data?.message || error.message || 'Failed to upload files');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Download attachment using the new API with Bearer token
  const handleDownload = async (attachment) => {
    const { attachmentId, name } = attachment;
    
    if (!attachmentId) {
      setUploadError("Attachment ID is not available for download.");
      return;
    }

    setDownloadingId(attachmentId);
    setUploadError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error("Authentication required. Please sign in.");
      }

      const downloadUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INCIDENT_ATTACHMENT_DOWNLOAD(attachmentId)}`;
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Download failed with status ${response.status}`);
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'attachment';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

    } catch (error) {
      console.error('Error downloading file:', error);
      setUploadError(error.message || 'Failed to download file');
    } finally {
      setDownloadingId(null);
    }
  };

  // Soft delete attachment using the new API
  const handleDelete = async (attachment) => {
    const { id, attachmentId, name } = attachment;
    
    if (!attachmentId) {
      setUploadError("Attachment ID is not available for deletion.");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    setDeletingId(attachmentId);
    setUploadError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error("Authentication required. Please sign in.");
      }

      const deleteUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INCIDENT_ATTACHMENT_DELETE(attachmentId)}`;
      
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 204 || response.ok) {
        // Remove from local state
        setAttachments((prev) => prev.filter((att) => att.id !== id));
        console.log('Attachment deleted successfully');
      } else {
        const errorText = await response.text();
        throw new Error(errorText || `Delete failed with status ${response.status}`);
      }

    } catch (error) {
      console.error('Error deleting attachment:', error);
      setUploadError(error.message || 'Failed to delete attachment');
    } finally {
      setDeletingId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type === 'application/pdf') return '📄';
    if (type.startsWith('text/')) return '📝';
    if (type.includes('word')) return '📄';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    return '📎';
  };

  return (
    <div className="attachments">
      {/* Upload Section */}
      {/* <div className="attachments__section">
        <div className="attachments__header">
          <h3 className="attachments__section-title">Upload Files</h3>
          <label className="btn btn--primary" htmlFor="file-upload">
            <Upload size={16} /> Upload Files
          </label>
          <input
            id="file-upload"
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>
      </div> */}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="upload-modal">
          <div className="upload-modal__overlay" onClick={() => !isUploading && setShowUploadModal(false)}></div>
          <div className="upload-modal__content">
            <div className="upload-modal__header">
              <h3>Upload Files</h3>
              {!isUploading && (
                <button className="upload-modal__close" onClick={() => setShowUploadModal(false)}>
                  <X size={20} />
                </button>
              )}
            </div>
            <div className="upload-modal__body">
              {selectedFiles.map((file, index) => (
                <div key={index} className="upload-file-item">
                  <div className="upload-file-item__info">
                    <span className="upload-file-item__name">{file.name}</span>
                    <span className="upload-file-item__size">{formatFileSize(file.size)}</span>
                  </div>
                </div>
              ))}

              {/* Error Message */}
              {uploadError && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #ef4444',
                  borderRadius: '8px',
                  color: '#991b1b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '12px'
                }}>
                  <AlertCircle size={20} />
                  <span>{uploadError}</span>
                </div>
              )}

              {/* Success Message */}
              {uploadSuccess && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#d1fae5',
                  border: '1px solid #10b981',
                  borderRadius: '8px',
                  color: '#065f46',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '12px'
                }}>
                  <Check size={20} />
                  <span>Files uploaded successfully!</span>
                </div>
              )}

              {/* Upload Progress */}
              {isUploading && (
                <div className="upload-progress">
                  <div className="upload-progress__bar">
                    <div
                      className="upload-progress__fill"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <span className="upload-progress__text">{uploadProgress}%</span>
                </div>
              )}
            </div>
            {!isUploading && (
              <div className="upload-modal__footer">
                <button className="btn btn--outline" onClick={() => setShowUploadModal(false)}>
                  Cancel
                </button>
                <button className="btn btn--primary" onClick={handleUpload}>
                  <Upload size={16} /> Upload
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Alert for Download/Delete */}
      {uploadError && !showUploadModal && (
        <div className="attachments__error">
          <AlertCircle size={16} />
          <span>{uploadError}</span>
          <button 
            className="attachments__error-close" 
            onClick={() => setUploadError(null)}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Attachments List */}
      <div className="attachments__section">
        <div className="attachments__section-header">
          <h3 className="attachments__section-title">
            Attachments ({attachments.length})
          </h3>
          <label className="btn btn--primary" htmlFor="file-upload">
            <Upload size={16} /> Upload Files
          </label>
          <input
            id="file-upload"
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>
        {attachments.length === 0 ? (
          <div className="attachments__empty">
            <File size={48} className="attachments__empty-icon" />
            <p className="attachments__empty-text">No attachments uploaded yet</p>
          </div>
        ) : (
          <div className="attachments-list">
            {attachments.map((attachment) => (
              <div key={attachment.id} className="attachment-card">
                <div className="attachment-card__icon">
                  {getFileIcon(attachment.type)}
                </div>
                <div className="attachment-card__info">
                  <div className="attachment-card__name">{attachment.name}</div>
                  <div className="attachment-card__meta">
                    <span>{attachment.size}</span>
                    <span>•</span>
                    <span>Uploaded by {attachment.uploadedBy}</span>
                    <span>•</span>
                    <span>{attachment.uploadedAt}</span>
                  </div>
                </div>
                <div className="attachment-card__actions">
                  <button
                    className="btn btn--icon"
                    onClick={() => handleDownload(attachment)}
                    disabled={downloadingId === attachment.attachmentId}
                    title="Download"
                  >
                    {downloadingId === attachment.attachmentId ? (
                      <Loader2 size={16} className="spinning" />
                    ) : (
                      <Download size={16} />
                    )}
                  </button>
                  <button
                    className="btn btn--icon btn--danger"
                    onClick={() => handleDelete(attachment)}
                    disabled={deletingId === attachment.attachmentId}
                    title="Delete"
                  >
                    {deletingId === attachment.attachmentId ? (
                      <Loader2 size={16} className="spinning" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

